import React, { useEffect, useState } from "react";
import { PlusCircle, XCircle, Save, Trash2, Image } from "lucide-react";

// In a real application, you would put this in a config file
// API path remains the same
const API_BASE_URL = "https://admin.theporch.in/api/categories"; 

// --- CSS Styles as a Template Literal ---
const CATEGORIES_CSS = `
/* --- Global Reset/Base (Simplified) --- */
*, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

/* 2. ⚠️ CONTAINER COLOR TRANSPARENT */
/* Assuming 'page-container' or body/main app element might be the outer container. 
   We will ensure the categories section itself has no background. */
.categories-section {
    margin-bottom: 0rem;
    /* Explicitly setting background to transparent for the main section */
    background-color: transparent; 
}

/* 1. ⚠️ SCROLLBAR REMOVAL & 2. ⚠️ SIZE REDUCTION - Key Changes */

/* This container now prevents scrolling and manages overflow */
.categories-scroll-container {
    overflow-x: hidden; /* 3. ✅ ENSURE SCROLLBAR IS HIDDEN */
    width: 100%;
    background-color: transparent; /* Ensuring inner container is also transparent */
}

.categories-grid-wrapper {
    display: flex;
    gap: 1rem; 
    padding: 0 1rem;
    align-items: flex-start;
    min-width: fit-content;
}

.no-categories-text {
    color: #6b7280;
    text-align: center;
    padding: 2rem 0;
    font-size: 1.125rem;
    width: 100%;
    flex-shrink: 0;
}

/* --- Category Card --- */
.category-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s ease-in-out; 
    flex-shrink: 0;
    width: 5.5rem; 
}
.category-card:hover {
    transform: scale(1.05);
}
@media (min-width: 768px) {
    .category-card {
        width: 6.5rem;
    }
}

.category-image-wrapper {
    width: 5.5rem;
    height: 5.5rem;
    border-radius: 9999px;
    overflow: hidden;
    /* 1. 🌟 COLORED BORDER ADDED */
    border: 3px solid #3b82f6; /* Thicker, colored border (Blue-600) */
    box-shadow: 0 4px 10px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.1); /* Slight blue glow shadow */
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white; /* Still need a solid background for the placeholder */
}

.category-card:hover .category-image-wrapper {
    border-color: #2563eb; /* Darker blue on hover */
    box-shadow: 0 4px 10px -1px rgba(59, 130, 246, 0.6), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
}

@media (min-width: 768px) {
    .category-image-wrapper {
        width: 6.5rem;
        height: 6.5rem;
    }
}

.category-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.no-image-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    text-align: center;
}

.no-image-text {
    font-size: 0.75rem;
    margin-top: 0.25rem;
    font-weight: 500;
}

.category-name-text {
    margin-top: 0.5rem;
    text-align: center;
    font-size: 0.75rem;
    line-height: 1.25rem;
    font-weight: 600;
    color: #4b5563;
}

@media (min-width: 768px) {
    .category-name-text {
        font-size: 0.875rem;
    }
}

/* --- Add Card (Matching Border Style) --- */
.add-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
    flex-shrink: 0;
    width: 5.5rem;
}
.add-card:hover {
    transform: scale(1.05);
}
@media (min-width: 768px) {
    .add-card {
        width: 6.5rem;
    }
}

.add-card-circle {
    width: 5.5rem;
    height: 5.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    /* 1. 🌟 Dotted/Dashed border now uses the color */
    border: 3px dashed #3b82f6; 
    border-radius: 9999px; 
    background-color: white; 
    color: #3b82f6; /* Matching icon color */
    transition: border-color 0.2s, color 0.2s, box-shadow 0.2s;
    box-shadow: 0 4px 10px -1px rgba(59, 130, 246, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
}

.add-card:hover .add-card-circle {
    border-color: #2563eb; 
    color: #2563eb; 
}

@media (min-width: 768px) {
    .add-card-circle {
        width: 6.5rem;
        height: 6.5rem;
    }
}

.add-icon {
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; 
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

.add-card-text {
    margin-top: 0.5rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280; 
    transition: color 0.2s;
}

.add-card:hover .add-card-text {
    color: #3b82f6; 
}

@media (min-width: 768px) {
    .add-card-text {
        font-size: 0.875rem;
    }
}

/* --- Modal Component (No changes needed) --- */

.modal-overlay {
    position: fixed; 
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.75); /* Slightly darker for focus */
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999; /* Higher than any sidebar or topbar */
    padding: 1rem; 
    backdrop-filter: blur(8px); /* Increased blur to isolate the modal */
}

.modal-content {
    background-color: white; 
    border-radius: 0.75rem; 
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.1); 
    width: 100%; 
    max-width: 32rem; 
    overflow: hidden;
    transition: all 0.3s ease-in-out; 
    border: 1px solid #e5e7eb; 
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem; 
    border-bottom: 1px solid #f3f4f6; 
    background-color: #f9fafb; 
}

.modal-title {
    font-size: 1.25rem; 
    line-height: 1.75rem;
    font-weight: 600; 
    color: #1f2937; 
}

.modal-close-button {
    color: #6b7280; 
    transition: color 0.2s; 
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.modal-close-button:hover {
    color: #ef4444; 
}

.modal-body {
    padding: 1.5rem; 
}

/* --- Form Styles --- */
.form-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem; 
}

.input-label {
    display: block; 
    font-size: 0.875rem; 
    font-weight: 500; 
    color: #374151; 
    margin-bottom: 0.25rem;
}

.text-input {
    display: block;
    width: 100%; 
    border: 1px solid #d1d5db; 
    border-radius: 0.5rem; 
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); 
    padding: 0.75rem; 
    transition: border-color 0.15s, box-shadow 0.15s; 
}
.text-input:focus {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #3b82f6; 
    box-shadow: 0 0 0 1px #3b82f6; 
}

/* Custom File Input Styling */
.file-input {
    display: block;
    width: 100%;
    font-size: 0.875rem; 
    color: #6b7280; 
    cursor: pointer;
}

.file-input::file-selector-button {
    margin-right: 1rem; 
    padding: 0.5rem 1rem; 
    border-radius: 9999px; 
    border: 0; 
    font-size: 0.875rem; 
    font-weight: 600; 
    background-color: #eff6ff; 
    color: #1d4ed8; 
    transition: background-color 0.2s;
    cursor: pointer;
}

.file-input::file-selector-button:hover {
    background-color: #dbeafe; 
}

/* Image Info Box */
.image-info-box {
    margin-top: 1rem; 
    padding: 0.75rem; 
    background-color: #f9fafb; 
    border-radius: 0.5rem; 
    border: 1px solid #e5e7eb; 
}

.flex-items-center {
    display: flex;
    align-items: center;
    gap: 0.75rem; 
}

.current-image-preview {
    width: 2.5rem; 
    height: 2.5rem; 
    object-fit: cover;
    border-radius: 0.375rem; 
}

.current-image-text {
    font-size: 0.875rem; 
    color: #4b5563; 
}

.current-image-link {
    color: #3b82f6; 
    font-weight: 500;
    margin-left: 0.25rem;
    text-decoration: none;
}
.current-image-link:hover {
    text-decoration: underline;
}

.no-current-image-text {
    font-size: 0.875rem; 
    color: #6b7280; 
}

.new-file-selected-text {
    margin-top: 0.5rem; 
    font-size: 0.75rem; 
    color: #059669; 
    font-weight: 500; 
}

/* --- Form Actions --- */
.form-actions {
    padding-top: 1.5rem; 
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #f3f4f6; 
}

.edit-actions-group {
    margin-left: auto; 
    display: flex;
    gap: 0.75rem; 
}

.add-actions-group {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.delete-button {
    display: flex;
    align-items: center;
    gap: 0.25rem; 
    background-color: #ef4444; 
    color: white;
    padding: 0.5rem 1rem; 
    border-radius: 0.5rem; 
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1); 
}
.delete-button:hover:not(:disabled) {
    background-color: #dc2626; 
}
.delete-button:disabled {
    background-color: #9ca3af; 
    cursor: not-allowed;
}

.cancel-button {
    padding: 0.5rem 1rem; 
    color: #374151; 
    border: 1px solid #d1d5db; 
    border-radius: 0.5rem; 
    background-color: white;
    cursor: pointer;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); 
}
.cancel-button:hover:not(:disabled) {
    background-color: #f3f4f6; 
}
.cancel-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.submit-button {
    display: flex;
    align-items: center;
    gap: 0.5rem; 
    background-color: #2563eb; 
    color: white;
    padding: 0.5rem 1rem; 
    border-radius: 0.5rem; 
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1); 
}
.submit-button:hover:not(:disabled) {
    background-color: #1d4ed8; 
}
.submit-button:disabled {
    background-color: #9ca3af; 
    cursor: not-allowed;
}
`;
// --- END CSS Styles ---

// Hook to inject the CSS dynamically
const useCssInjection = (css) => {
    useEffect(() => {
        if (document.getElementById('categories-bar-style')) {
            return;
        }

        const style = document.createElement('style');
        style.id = 'categories-bar-style';
        style.type = 'text/css';
        style.appendChild(document.createTextNode(css));
        document.head.appendChild(style);

        return () => {
            const injectedStyle = document.getElementById('categories-bar-style');
            if (injectedStyle) {
                document.head.removeChild(injectedStyle);
            }
        };
    }, [css]);
};

// --- Utility Component: Modal (Unchanged) ---

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button
            onClick={onClose}
            className="modal-close-button"
          >
            <XCircle size={24} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
};

// --- Main Component: CategoriesBar ---

export default function CategoriesBar() {
  // Inject the CSS styles
  useCssInjection(CATEGORIES_CSS);
  
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({ id: null, name: "", image: "" });
  // New state to hold the Base64 data of the newly selected file
  const [base64Image, setBase64Image] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [fileError, setFileError] = useState('');


  // 1. Data Fetching
  const fetchCategories = async () => {
    try {
      const res = await fetch(API_BASE_URL);
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // 2. Form & Modal Handlers
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });
    
  // Converts file to Base64 and stores it
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setBase64Image(null); // Clear previous base64
    setSelectedFileName('');
    setFileError('');

    if (file) {
      // Client-side file size validation (2MB limit for Base64 storage)
      if (file.size > 1024 * 1024 * 2) { 
        setFileError('Image file must be less than 2MB.');
        e.target.value = null; // Clear the input
        return;
      }
      
      setSelectedFileName(file.name);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        // reader.result is the Base64 string (e.g., data:image/jpeg;base64,...)
        setBase64Image(reader.result); 
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setForm({ id: null, name: "", image: "" });
    setBase64Image(null); // Clear Base64 image data
    setSelectedFileName('');
    setFileError('');
    const fileInput = document.getElementById("image-upload-modal");
    if (fileInput) fileInput.value = null;
  };

  const openEditModal = (cat) => {
    setForm(cat);
    setBase64Image(null); 
    setSelectedFileName('');
    setFileError('');
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // 3. CRUD Operations
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (fileError) return;

    // Check if new category has both name and an image
    // NOTE: This check should be adjusted if clearing an existing image is supported, 
    // but for now, we maintain the check that all NEW categories must have an image.
    if (!form.name || (!form.id && !base64Image)) {
      alert("Please provide a name and an image for new categories.");
      return;
    }

    setLoading(true);
    
    // Payload for JSON body
    let payload = {
        name: form.name,
        // FIX APPLIED HERE: Change 'image_url' to the correct key 'image'
        // Logic: If base64Image is set (new file selected), use it. 
        // Otherwise, use the existing form.image (Base64 string from DB).
        image: base64Image || form.image || null, 
    };
    
    let url = API_BASE_URL;
    let method = "POST";

    if (form.id) {
      url = `${API_BASE_URL}/${form.id}`;
      method = "PUT";
    }

    try {
      const res = await fetch(url, { 
          method, 
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload) // Send JSON body
      });
      
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "API error");
      }

      handleModalClose(); // Close modal and reset
      fetchCategories();
    } catch (err) {
      console.error(
        `Error ${form.id ? "updating" : "adding"} category:`,
        err
      );
      alert(`Operation failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this category?"
      )
    )
      return;

    try {
      // Backend deletion no longer needs to handle file system
      const res = await fetch(`${API_BASE_URL}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      fetchCategories();
      handleModalClose(); // Close modal after successful delete
    } catch (err) {
      console.error("Error deleting category:", err);
      alert("Deletion failed.");
    }
  };

  // 4. Render Components
  const CategoryCard = ({ cat }) => {
    // The cat.image property now directly contains the Base64 string
    const imageUrl = cat.image || null; 

    return (
      <div
        className="category-card"
        onClick={() => openEditModal(cat)}
      >
        {/* Circle Image */}
        <div className="category-image-wrapper">
          {imageUrl ? (
            // Use the Base64 string as the image source
            <img
              src={imageUrl} 
              alt={cat.name}
              className="category-image"
              // Optional: Handle error if Base64 is corrupted
              onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="no-image-placeholder">
              <Image size={24} /> 
              <span className="no-image-text">No Image</span>
            </div>
          )}
        </div>

        {/* Name Below */}
        <p className="category-name-text">
          {cat.name}
        </p>
      </div>
    );
  };

  const AddCard = () => {
    return (
      <div
        className="add-card"
        onClick={openAddModal}
      >
        <div className="add-card-circle">
          <PlusCircle size={32} className="add-icon" /> 
        </div>
        <p className="add-card-text">
          Add New
        </p>
      </div>
    );
  };

  // JSX structure places AddCard first
  return (
    <div className="page-container">
      {/* Categories Section */}
      <div className="categories-section">
        
        {/* Scroll Container: overflow-x: hidden to remove scroll bar */}
        <div className="categories-scroll-container">
          {/* Categories Grid Wrapper: Handles display: flex and gap */}
          <div className="categories-grid-wrapper">
            
            {/* Add New Category Card (First) */}
            <AddCard />

            {/* Categories Mapping */}
            {categories.length > 0 && 
              categories.map((cat) => <CategoryCard key={cat.id} cat={cat} />)
            }
          </div>
          {/* Fallback text only if no categories */}
          {categories.length === 0 && (
             <p className="no-categories-text">
                Start by adding your first category!
             </p>
          )}

        </div>
      </div>

      {/* Edit/Add Modal - unchanged */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={form.id ? `✏️ Edit Category: ${form.name}` : "➕ Add New Category"}
      >
        <form onSubmit={handleSubmit} className="form-container">
          {/* Name Input */}
          <div>
            <label
              htmlFor="name-input"
              className="input-label"
            >
              Category Name
            </label>
            <input
              type="text"
              id="name-input"
              name="name"
              placeholder="e.g., Desserts"
              value={form.name}
              onChange={handleChange}
              className="text-input"
              required
            />
          </div>

          {/* File Input */}
          <div>
            <label
              htmlFor="image-upload-modal"
              className="input-label mb-2"
            >
              {form.id
                ? "New Image (Optional - Overwrites Current)"
                : "Category Image"}
            </label>
            <input
              type="file"
              id="image-upload-modal"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              // Only require image for new additions if no existing image is present
              required={!form.id && !form.image} 
            />

            {fileError && <p style={{ color: '#ef4444', fontSize: '0.8em', marginTop: '0.5rem' }}>{fileError}</p>}
            
            {/* Current Image Preview/Info */}
            <div className="image-info-box">
              {/* Show new file name or current image */}
              {selectedFileName ? (
                 <p className="new-file-selected-text">
                  New file selected: {selectedFileName}
                 </p>
              ) : form.id && form.image ? (
                <div className="flex-items-center space-x-3">
                  <img
                    // The source is now the Base64 string from the DB
                    src={form.image} 
                    alt="Current"
                    className="current-image-preview"
                  />
                  <span className="current-image-text">
                    Current Image is stored in DB.
                  </span>
                </div>
              ) : (
                <p className="no-current-image-text">
                  No current image available.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            {form.id && (
              <button
                type="button"
                onClick={() => handleDelete(form.id)}
                className="delete-button"
                disabled={loading}
              >
                <Trash2 size={18} />
                <span>Delete</span>
              </button>
            )}
            <div
              className={
                form.id ? "edit-actions-group" : "add-actions-group"
              }
            >
              <button
                type="button"
                onClick={handleModalClose}
                className="cancel-button"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-button"
                // Require name AND (Base64 image OR existing image ID)
                disabled={loading || !form.name || (!form.id && !base64Image) || fileError} 
              >
                {loading ? (
                  "Processing..."
                ) : form.id ? (
                  <>
                    <Save size={18} />
                    <span>Update</span>
                  </>
                ) : (
                  <>
                    <PlusCircle size={18} />
                    <span>Add Category</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </Modal>

    </div>
  );
}
